import { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { selectAccessToken } from "../store/auth";
import * as mediaContentApi from "../api/mediaContent";
import { useTranslation } from "../i18n";

const MediaContentManager = ({ files, onFilesChange }) => {
  const { t } = useTranslation();
  const accessToken = useSelector(selectAccessToken);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [renaming, setRenaming] = useState(null);
  const [newName, setNewName] = useState("");

  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  const handleFileUpload = async (event) => {
    const uploadedFiles = event.target.files;
    if (!uploadedFiles || uploadedFiles.length === 0) return;

    setUploading(true);
    try {
      const uploadPromises = Array.from(uploadedFiles).map((file) =>
        mediaContentApi.uploadFile(accessToken, file),
      );
      await Promise.all(uploadPromises);
      toast.success(
        uploadedFiles.length === 1
          ? t("fileManager.uploadSuccess")
          : `${t("fileManager.filesUploaded")}: ${uploadedFiles.length}`,
      );
      onFilesChange();
    } catch (error) {
      toast.error(error.message || t("fileManager.uploadFilesError"));
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleFileOpen = (fileName) => {
    const HOST = import.meta.env.VITE_API_HOST;
    const url = `${HOST}/api/v1/media-content/file/${fileName}`;
    window.open(url, "_blank");
  };

  const handleContextMenu = (event, fileName) => {
    event.preventDefault();
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      fileName,
    });
  };

  const handleDelete = async (fileName) => {
    if (!window.confirm(t("confirm.deleteFile", { fileName }))) return;

    try {
      await mediaContentApi.deleteFile(accessToken, fileName);
      toast.success(t("fileManager.deleteSuccess"));
      onFilesChange();
    } catch (error) {
      toast.error(error.message || t("fileManager.deleteError"));
    }
    setContextMenu(null);
  };

  const handleRename = (fileName) => {
    setRenaming(fileName);
    setNewName(fileName);
    setContextMenu(null);
  };

  const confirmRename = async () => {
    if (!renaming || !newName) return;

    try {
      await mediaContentApi.renameFile(accessToken, renaming, newName);
      toast.success(t("fileManager.renameSuccess"));
      onFilesChange();
      setRenaming(null);
      setNewName("");
    } catch (error) {
      toast.error(error.message || t("fileManager.renameError"));
    }
  };

  const cancelRename = () => {
    setRenaming(null);
    setNewName("");
  };

  return (
    <div className="space-y-4">
      <div className="bg-c-gray-600 rounded-lg shadow p-6">
        <h2 className="text-2xl mb-4">{t("mediaManager.uploadTitle")}</h2>
        <div className="flex items-center gap-4">
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileUpload}
            disabled={uploading}
            multiple
            className="flex-1 text-sm text-c-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-accent file:text-white hover:file:bg-accent-hover cursor-pointer"
            accept="image/*,video/*"
          />
          {uploading && (
            <span className="text-c-gray-400">{t("common.loading")}</span>
          )}
        </div>
      </div>

      <div className="bg-c-gray-600 rounded-lg shadow p-6">
        <h2 className="text-2xl mb-4">{t("mediaManager.files")}</h2>
        {files.length === 0 ? (
          <p className="text-c-gray-400">{t("mediaManager.noFiles")}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {files.map((fileName) => (
              <div
                key={fileName}
                className="bg-c-gray-500 border border-c-gray-400/20 rounded-lg p-4 hover:bg-c-gray-500/80 transition-colors"
                onContextMenu={(e) => handleContextMenu(e, fileName)}
              >
                {renaming === fileName ? (
                  <div
                    className="space-y-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="text"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      className="w-full px-2 py-1 bg-c-gray-700 border border-c-gray-400/30 rounded text-gray-100 focus:outline-none focus:border-accent"
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={confirmRename}
                        className="px-3 py-1 bg-c-green-500 hover:bg-c-green-600 text-white rounded text-sm transition-colors"
                      >
                        {t("common.save")}
                      </button>
                      <button
                        onClick={cancelRename}
                        className="px-3 py-1 bg-c-gray-400 hover:bg-c-gray-400/80 text-white rounded text-sm transition-colors"
                      >
                        {t("common.cancel")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <svg
                      className="w-6 h-6 text-c-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                      />
                    </svg>
                    <span className="text-sm truncate flex-1">{fileName}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {contextMenu && (
        <div
          className="fixed bg-c-gray-500 border border-c-gray-400/30 rounded-lg shadow-lg py-2 z-50"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => {
              handleFileOpen(contextMenu.fileName);
              setContextMenu(null);
            }}
            className="w-full px-4 py-2 text-left hover:bg-c-gray-600 text-sm transition-colors"
          >
            {t("common.open")}
          </button>
          <button
            onClick={() => handleRename(contextMenu.fileName)}
            className="w-full px-4 py-2 text-left hover:bg-c-gray-600 text-sm transition-colors"
          >
            {t("common.rename")}
          </button>
          <button
            onClick={() => handleDelete(contextMenu.fileName)}
            className="w-full px-4 py-2 text-left hover:bg-c-gray-600 text-c-red-500 text-sm transition-colors"
          >
            {t("common.delete")}
          </button>
        </div>
      )}
    </div>
  );
};

export default MediaContentManager;
